import { useEffect, useState } from 'react';
import myShopClient from '@services/nearlive';
import { SESSION_STORAGE_KEYS } from '../constants';

const OAUTH_POLLING_EVENT = 'oauth-callback-complete';

/**
 * Handles OAuth callback from URL parameters
 * Self-contained hook that reads session IDs from sessionStorage
 * and communicates with other hooks via custom events
 *
 * @returns {Object} { isProcessing }
 */
export const useOAuthCallback = () => {
	const [isProcessing, setIsProcessing] = useState(false);

	useEffect(() => {
		const handleOAuthCallback = async () => {
			const urlParams = new URLSearchParams(window.location.search);
			const code = urlParams.get('code');

			if (!code) {
				return;
			}

			const stockSessionId = sessionStorage.getItem(SESSION_STORAGE_KEYS.STOCK_SESSION);
			const productSessionId = sessionStorage.getItem(SESSION_STORAGE_KEYS.PRODUCT_SESSION);

			if (!stockSessionId && !productSessionId) {
				return;
			}

			const sessionId = stockSessionId || productSessionId;
			const connectionKind = stockSessionId === sessionId ? 'stock' : 'product';

			setIsProcessing(true);

			try {
				const client = await myShopClient();
				await client.post(`/api/onboarding/connect-sessions/${sessionId}/oauth`, {
					code,
					state: urlParams.get('state'),
					...Object.fromEntries(urlParams.entries())
				});

				const cleanUrl = window.location.pathname + window.location.search
					.replace(/[?&](code|state|error|error_description)=[^&]*/g, '')
					.replace(/^&/, '?')
					.replace(/\?$/, '');
				window.history.replaceState({}, '', cleanUrl);

				window.dispatchEvent(new CustomEvent(OAUTH_POLLING_EVENT, {
					detail: { connectionKind, success: true }
				}));

				setIsProcessing(false);
			} catch (err) {
				const cleanUrl = window.location.pathname + window.location.search
					.replace(/[?&](code|state|error|error_description)=[^&]*/g, '')
					.replace(/^&/, '?')
					.replace(/\?$/, '');
				window.history.replaceState({}, '', cleanUrl);

				window.dispatchEvent(new CustomEvent(OAUTH_POLLING_EVENT, {
					detail: {
						connectionKind,
						success: false,
						error: err.response?.data?.error?.message || err.message || 'OAuth authorisation failed. Please try again.'
					}
				}));

				setIsProcessing(false);
			}
		};

		handleOAuthCallback();
	}, []);

	return {
		isProcessing
	};
};

export { OAUTH_POLLING_EVENT };
